import React, { useCallback, useState } from 'react';

import {
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	pointerWithin,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { BarChart3, GripVertical } from 'lucide-react';

import {
	useBulkUpdateLineItemsMutation,
	useUpdateLineItemMutation,
} from '../../api';
import { useToast } from '../../context/ToastContext';
import { formatCurrency } from '../../utils/utils';
import AnalysisTable from '../AnalysisTable';
import { SkeletonMetric, SkeletonTable } from '../common';

import type { BulkLineItemUpdate } from '../../api';
import type {
	UnifiedAnalysisData,
	UnifiedFeeItem,
	UnifiedOtherService,
} from '../../types';
import type {
	CollisionDetection,
	DragEndEvent,
	DragStartEvent,
} from '@dnd-kit/core';

/**
 * Custom collision detection that intelligently chooses between line-item and category drop targets.
 *
 * Rules:
 * 1. If dropping near the top of a section (section header area), prefer category (root item drop)
 * 2. If dropping on a line item row, prefer line-item (child drop)
 * 3. This allows users to drop items as root items when dropping in the section header area
 */
const preferLineItemCollision: CollisionDetection = (args) => {
	// First, get all collisions using closestCenter
	const closestCenterCollisions = closestCenter(args);

	// Also get collisions using pointerWithin for more precise detection
	const pointerWithinCollisions = pointerWithin(args);

	// Combine all unique collisions
	const allCollisions = [...closestCenterCollisions];
	pointerWithinCollisions.forEach((collision) => {
		if (!allCollisions.find((c) => c.id === collision.id)) {
			allCollisions.push(collision);
		}
	});

	// If no collisions, return empty
	if (allCollisions.length === 0) {
		return closestCenterCollisions;
	}

	// Separate line-item collisions, category collisions, and gap collisions
	const lineItemCollisions = allCollisions.filter((collision) =>
		String(collision.id).startsWith('line-item-'),
	);
	const categoryCollisions = allCollisions.filter((collision) =>
		String(collision.id).startsWith('category_'),
	);
	const gapCollisions = allCollisions.filter((collision) =>
		String(collision.id).startsWith('gap-'),
	);

	// Check if pointer is directly within a line-item (hovering directly over the row)
	// If so, prioritize line-item collision to show "drop as child"
	const lineItemPointerWithin = pointerWithinCollisions.filter((collision) =>
		String(collision.id).startsWith('line-item-'),
	);

	// If pointer is directly within a line-item, prioritize it over gap collisions
	// This ensures "drop as child" shows when hovering over parent rows
	if (lineItemPointerWithin.length > 0 && gapCollisions.length > 0) {
		lineItemPointerWithin.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});
		console.log(
			'Pointer directly over line-item, preferring child drop:',
			lineItemPointerWithin[0].id,
		);
		return [lineItemPointerWithin[0]];
	}

	// Handle gap vs line-item collisions intelligently
	// Prefer gap unless line-item is MUCH closer (4x closer) - this makes gap zones more discoverable
	if (gapCollisions.length > 0 && lineItemCollisions.length > 0) {
		gapCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});
		lineItemCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});

		const closestGap = gapCollisions[0];
		const closestLineItem = lineItemCollisions[0];

		const gapDistance = (closestGap.data?.value as number) ?? Infinity;
		const lineItemDistance =
			(closestLineItem.data?.value as number) ?? Infinity;

		// Prefer gap unless line-item is MUCH closer (4x closer) - means we're hovering directly on the item
		// This makes gap zones more discoverable and easier to use
		if (lineItemDistance * 4 < gapDistance) {
			console.log(
				'Preferring line-item collision (very close):',
				closestLineItem.id,
			);
			return [closestLineItem];
		} else {
			console.log('Preferring gap collision (drop as parent):', closestGap.id, {
				gapDistance,
				lineItemDistance,
				ratio: lineItemDistance / gapDistance,
			});
			return [closestGap];
		}
	}

	// If we only have gap collisions, use them
	if (gapCollisions.length > 0) {
		gapCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});
		return [gapCollisions[0]];
	}

	// If we have both category and line-item collisions, check if we should prefer category
	// This happens when dropping near the section header (top of the section) or above the first item
	if (categoryCollisions.length > 0 && lineItemCollisions.length > 0) {
		// Get the closest category and line-item collisions
		categoryCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});
		lineItemCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});

		const closestCategory = categoryCollisions[0];
		const closestLineItem = lineItemCollisions[0];

		const categoryDistance =
			(closestCategory.data?.value as number) ?? Infinity;
		const lineItemDistance =
			(closestLineItem.data?.value as number) ?? Infinity;

		// Prefer category (root item drop) when:
		// 1. Category is closer than line-item, OR
		// 2. Line-item is not significantly closer (within 3x distance for more aggressive root drops)
		// This allows dropping as root item when near section header or above first item
		// The threshold of 3x means: if line-item is more than 3x closer, prefer it (child drop)
		// Otherwise, prefer category (root drop) - this makes it easier to drop in section header area
		// We use a higher threshold (3x) to make it easier to drop as root items in the green box area
		if (categoryDistance <= lineItemDistance * 3) {
			return [closestCategory];
		}
	}

	// If we have line-item collisions and no category, or category is far, prefer line-item
	if (lineItemCollisions.length > 0) {
		// Sort by distance (data.value is the distance metric from closestCenter)
		lineItemCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});
		return [lineItemCollisions[0]];
	}

	// Otherwise, return category collisions
	if (categoryCollisions.length > 0) {
		categoryCollisions.sort((a, b) => {
			const aValue = (a.data?.value as number) ?? Infinity;
			const bValue = (b.data?.value as number) ?? Infinity;
			return aValue - bValue;
		});
		return [categoryCollisions[0]];
	}

	// Return the original closestCenter result as fallback
	return closestCenterCollisions;
};

const AnalysisSectionSkeleton: React.FC<{
	title: string;
	skeletonRows: number;
}> = ({ title, skeletonRows }) => (
	<div className="bg-white flex flex-col justify-start items-start">
		<div className="w-full px-7 py-4 flex flex-col justify-start items-start">
			<div className="text-black text-base font-semibold leading-tight">
				{title}
			</div>
		</div>
		<div className="w-full px-7 pb-6">
			<SkeletonTable rows={skeletonRows} columns={8} showHeader={true} />
		</div>
	</div>
);

const GrandTotalSection: React.FC<{
	metrics: Array<{ label: string; value: string; valueColor: string }>;
}> = ({ metrics }) => (
	<div className="w-full mt-4 px-5 py-5 bg-black/5 border-t border-black/5 inline-flex justify-start items-center">
		<div className="w-full flex justify-start items-center gap-6">
			{metrics.map((item, index) => (
				<div
					key={index}
					className="w-full px-4 rounded-md flex justify-center items-center gap-2"
				>
					<div className="flex-1 inline-flex flex-col justify-center items-center gap-1">
						<div className="self-stretch text-center justify-center text-zinc-500 text-xs font-medium uppercase leading-none">
							{item.label}
						</div>
						<div className="self-stretch flex flex-col justify-center items-start gap-1">
							<div
								className={`self-stretch text-center justify-center text-2xl font-bold ${item.valueColor}`}
							>
								{item.value}
							</div>
						</div>
					</div>
				</div>
			))}
		</div>
	</div>
);

interface AnalysisStepProps {
	status: string;
	showProgress: boolean;
	isLoading: boolean;
	isFetching: boolean;
	analysisData: UnifiedAnalysisData;
	document: {
		id: string;
		processingMonth: string;
	};
	monthlySavingsData: {
		value: number;
		isNegative: boolean;
	};
	// Backend-calculated totals (statement-type-aware)
	calculatedTotals?: {
		ic: number;
		icPlus: number;
		savings: {
			monthly: number;
			yearly: number;
			percent_lower: number;
			is_negative: boolean;
		} | null;
		effectiveRates: {
			current: number;
			comparison: number;
		} | null;
		volume: number | null;
		calculationMethod: string | null;
		show_rate_disclaimer?: boolean; // True for flat-tiered statements
	};
	logsData?: any; // For progress calculation
	enableEditing?: boolean;
	onLineItemUpdated?: () => void;
	// View mode state - passed from parent to sync with header view switcher
	viewMode?: 'default' | 'user_edits' | 'customized_quote';
	onViewModeChange?: (
		view: 'default' | 'user_edits' | 'customized_quote',
	) => void;
}

const AnalysisStep: React.FC<AnalysisStepProps> = ({
	status,
	showProgress,
	isLoading,
	isFetching,
	analysisData,
	document,
	monthlySavingsData,
	calculatedTotals,
	logsData,
	enableEditing = false,
	onLineItemUpdated,
	viewMode: viewModeProp = 'default',
	onViewModeChange: onViewModeChangeProp,
}) => {
	// Toast for showing warnings
	const { showToast } = useToast();

	// View mode - use prop if provided, otherwise use local state for backwards compatibility
	const [localViewMode, setLocalViewMode] = useState<
		'default' | 'user_edits' | 'customized_quote'
	>('default');
	const viewMode = viewModeProp ?? localViewMode;
	const setViewMode = onViewModeChangeProp ?? setLocalViewMode;

	// Drag-and-drop state for cross-section moves
	const [activeId, setActiveId] = useState<string | null>(null);
	const [draggedItem, setDraggedItem] = useState<
		UnifiedFeeItem | UnifiedOtherService | null
	>(null);
	const [draggedFromCategory, setDraggedFromCategory] = useState<string | null>(
		null,
	);
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	// Track recently dropped items for temporary highlight (10 seconds)
	const [recentlyDroppedIds, setRecentlyDroppedIds] = useState<Set<string>>(
		new Set(),
	);

	// Track drop history for undo functionality (stack-based for multiple undo levels)
	interface DropHistoryItem {
		itemId: string;
		itemIdIcPlus: string | null;
		item: UnifiedFeeItem | UnifiedOtherService;
		previousSection: string;
		previousParentId: string | null;
		previousParentIdIcPlus: string | null;
		previousDisplayOrder: number | undefined;
		newSection: string;
		newParentId: string | null;
		newParentIdIcPlus: string | null;
		newDisplayOrder: number | undefined;
		timestamp: number;
	}
	const [dropHistory, setDropHistory] = useState<DropHistoryItem[]>([]);
	const [isUndoing, setIsUndoing] = useState(false);

	// Info modal for drag-drop validation messages
	const [infoModal, setInfoModal] = useState<{
		isOpen: boolean;
		title: string;
		message: string;
	}>({
		isOpen: false,
		title: '',
		message: '',
	});

	const handleToggleRow = useCallback((lineItemId: string) => {
		setExpandedRows((prev) => {
			const next = new Set(prev);
			if (next.has(lineItemId)) {
				next.delete(lineItemId);
			} else {
				next.add(lineItemId);
			}
			return next;
		});
	}, []);

	// Line item update mutation
	const [updateLineItem] = useUpdateLineItemMutation();
	const [bulkUpdateLineItems] = useBulkUpdateLineItemsMutation();

	// Handler for persistent undo (using move_history from extra_attributes)
	const handlePersistentUndo = useCallback(
		async (item: UnifiedFeeItem | UnifiedOtherService) => {
			const moveHistory = item.extra_attributes?.move_history;
			const hasMoveHistory =
				Array.isArray(moveHistory) && moveHistory.length > 0;
			if (isUndoing || !hasMoveHistory) {
				return;
			}

			setIsUndoing(true);

			try {
				// Use the update_line_item API with action: 'undo_move'
				// Use item.id (database UUID) if available, otherwise fall back to line_item_id
				// This handles IC+-only items where line_item_id might be empty
				const primaryItemId = item.id || item.line_item_id;

				if (!primaryItemId) {
					console.error('Cannot undo: item has no valid ID', { item });
					setIsUndoing(false);
					return;
				}

				// This will undo the last move from move_history in extra_attributes
				// NOTE: Backend's undo_move handles BOTH processor pair items (IC/IC+ or flat_tiered/flat_rate)
				// automatically, so we only need to call it once. The backend will:
				// 1. Find and restore the counterpart item
				// 2. Remove the last move from move_history for both items
				await updateLineItem({
					lineItemId: primaryItemId,
					fileId: document.id,
					updates: {
						action: 'undo_move',
					},
				}).unwrap();

				// Refresh data to show updated positions
				if (onLineItemUpdated) {
					onLineItemUpdated();
				}
			} catch (error) {
				console.error('Error undoing move:', error);
				// Refresh data even on error to ensure UI reflects actual server state
				if (onLineItemUpdated) {
					onLineItemUpdated();
				}
			} finally {
				setIsUndoing(false);
			}
		},
		[isUndoing, updateLineItem, document.id, onLineItemUpdated],
	);

	// Configure drag-and-drop sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	// Helper to get all descendant IDs
	const getAllDescendantIds = useCallback(
		(item: UnifiedFeeItem | UnifiedOtherService): string[] => {
			const ids: string[] = [];
			if (item.children && item.children.length > 0) {
				item.children.forEach((child) => {
					ids.push(child.line_item_id);
					if (child.children && child.children.length > 0) {
						ids.push(...getAllDescendantIds(child));
					}
				});
			}
			return ids;
		},
		[],
	);

	// Drag handlers
	const handleDragStart = useCallback((event: DragStartEvent) => {
		const { active } = event;
		setActiveId(active.id as string);

		// Extract category and item from active.data
		const data = active.data.current;
		if (data) {
			setDraggedItem(data.item);
			setDraggedFromCategory(data.categoryId);
		}
	}, []);

	// Define analysis sections data for mapping
	const analysisSections = [
		{
			title: 'DUES, FEES & ASSESSMENTS',
			sectionKey: 'dues_fees_assessments',
			data: analysisData.duesFeesAssessments,
			columns: ['fee', 'volume'],
			skeletonRows: 3,
		},
		{
			title: 'INTERCHANGE',
			sectionKey: 'interchange',
			data: analysisData.interchange,
			columns: ['perTrans', 'volume'],
			skeletonRows: 4,
		},
		{
			title: 'AUTHORIZATION & DISCOUNT FEES',
			sectionKey: 'authorization_and_discount',
			data: analysisData.authorizationAndDiscount,
			columns: ['perTrans', 'volume'],
			skeletonRows: 5,
		},
		{
			title: 'OTHER FEES',
			sectionKey: 'other_fees',
			data: analysisData.otherFees,
			columns: ['fee'],
			skeletonRows: 2,
		},
	];

	// Helper function to calculate display_order based on drop position
	const calculateDisplayOrder = (
		targetItem: UnifiedFeeItem | UnifiedOtherService | null,
		siblings: (UnifiedFeeItem | UnifiedOtherService)[],
		isAbove: boolean,
	): number => {
		if (!targetItem) {
			// Drop at end of section
			const maxOrder = Math.max(
				...siblings.map((s) => s.display_order ?? 0),
				0,
			);
			return maxOrder + 1;
		}

		const targetOrder = targetItem.display_order ?? 0;

		if (isAbove) {
			// Find previous sibling
			const prevSibling = siblings
				.filter((s) => (s.display_order ?? 0) < targetOrder)
				.sort((a, b) => (b.display_order ?? 0) - (a.display_order ?? 0))[0];

			if (prevSibling) {
				return (
					(prevSibling.display_order ?? 0) +
					(targetOrder - (prevSibling.display_order ?? 0)) / 2
				);
			}
			return targetOrder - 1;
		} else {
			// Find next sibling
			const nextSibling = siblings
				.filter((s) => (s.display_order ?? 0) > targetOrder)
				.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))[0];

			if (nextSibling) {
				return (
					targetOrder + ((nextSibling.display_order ?? 0) - targetOrder) / 2
				);
			}
			return targetOrder + 1;
		}
	};

	const handleDragEnd = useCallback(
		async (event: DragEndEvent) => {
			const { over, active } = event;

			setActiveId(null);
			const currentDraggedItem = draggedItem;
			const sourceCategory = draggedFromCategory;
			setDraggedItem(null);
			setDraggedFromCategory(null);

			if (!over || !currentDraggedItem) {
				return;
			}

			const overId = over.id as string;
			const overData = over.data.current;

			// Determine drop target type and extract info
			let sectionName: string;
			let newParentId: string | null = null;
			let newParentIdIcPlus: string | null = null;
			let targetParentForExpand: UnifiedFeeItem | UnifiedOtherService | null =
				null;
			let targetItem: UnifiedFeeItem | UnifiedOtherService | null = null;
			let isAbove: boolean = false;

			// Check if dragged item has children
			// Note: A parent item (can have children) without actual children can be moved anywhere
			// Only items that currently have children are restricted from being nested under other parents
			const draggedItemHasChildren =
				currentDraggedItem.children && currentDraggedItem.children.length > 0;

			if (overId.startsWith('gap-')) {
				// Dropping in a gap between items - add as root/parent item between the two items
				const gapData = overData;
				const targetCategoryId = gapData?.categoryId as string;
				sectionName = targetCategoryId?.replace('category_', '') || '';
				newParentId = null;
				newParentIdIcPlus = null;

				// Get items above and below the gap for display_order calculation
				const itemAbove = gapData?.itemAbove as
					| UnifiedFeeItem
					| UnifiedOtherService
					| undefined;
				const itemBelow = gapData?.itemBelow as
					| UnifiedFeeItem
					| UnifiedOtherService
					| undefined;

				// Use itemAbove as targetItem for display_order calculation (we're inserting after it)
				targetItem = itemAbove || null;
				isAbove = false; // We're inserting after itemAbove (below it)
			} else if (overId.startsWith('line-item-')) {
				// Dropping on a specific parent row - add as child of that parent

				// VALIDATION: Only items WITHOUT children can be dropped onto parent rows
				// - Parent items WITH children: can only be dropped on sections (to become root items)
				//   Reason: Moving a parent with children to another parent would create nested hierarchies
				// - Parent items WITHOUT children: can be dropped on other parents or sections (no restrictions)
				//   Reason: They can become children of other parents or remain as root items
				// - Regular items (not parents): can be dropped anywhere
				//   Reason: They can become children of any parent or remain as root items
				if (draggedItemHasChildren) {
					setInfoModal({
						isOpen: true,
						title: 'Cannot Move Parent Item',
						message:
							'Parent items with children can only be moved to sections, not nested under other parents. Try dropping on a section header instead.',
					});
					return;
				}
				// If we reach here, the item has no children (whether it's a parent or not),
				// so it can be dropped on this parent row to become a child

				if (overData?.type === 'line-item' && overData?.item) {
					targetItem = overData.item as UnifiedFeeItem | UnifiedOtherService;
					const targetCategoryId = overData.categoryId as string;

					// Get section name from the category
					sectionName = targetCategoryId?.replace('category_', '') || '';

					// Set the target parent - item will become a child of this parent
					// Use id (database UUID) if available, otherwise fall back to line_item_id
					// For IC+-only items, also check line_item_id_ic_plus as fallback
					// Handle empty strings by checking truthiness (empty string is falsy)
					newParentId =
						(targetItem.id && targetItem.id.trim()) ||
						(targetItem.line_item_id && targetItem.line_item_id.trim()) ||
						(targetItem.line_item_id_ic_plus &&
							targetItem.line_item_id_ic_plus.trim()) ||
						null;
					newParentIdIcPlus =
						(targetItem.line_item_id_ic_plus &&
							targetItem.line_item_id_ic_plus.trim()) ||
						null;
					targetParentForExpand = targetItem;

					if (!newParentId) {
						console.error('Target parent has no valid ID:', {
							overId,
							targetItem,
							targetItemId: targetItem.id,
							targetItemLineItemId: targetItem.line_item_id,
							targetItemLineItemIdIcPlus: targetItem.line_item_id_ic_plus,
							overData,
						});
						return;
					}

					// Detect drop position (above/below) using event delta
					// Positive delta.y means dropped below, negative means above
					const deltaY = event.delta?.y ?? 0;
					isAbove = deltaY < 0;
				} else {
					return;
				}
			} else if (overId.startsWith('category_')) {
				// Dropping on a section/category - add as root item
				sectionName = overId.replace('category_', '');
				newParentId = null;
				newParentIdIcPlus = null;
				targetItem = null; // No specific target item for section drops
				isAbove = false; // Not applicable for section drops
			} else {
				// Unknown drop target
				return;
			}

			// Prevent dropping on same category (only for category drops)
			if (
				overId.startsWith('category_') &&
				sourceCategory &&
				overId === sourceCategory
			) {
				return;
			}

			// Validate section name
			if (!sectionName) {
				return;
			}

			try {
				// Get all siblings in the target section for display_order calculation
				const getSiblingsInSection = (
					sectionKey: string,
				): (UnifiedFeeItem | UnifiedOtherService)[] => {
					const section = analysisSections.find(
						(s) => s.sectionKey === sectionKey,
					);
					if (!section || !section.data) return [];

					// Get all root items (items without parent_id) in this section
					const allItems: (UnifiedFeeItem | UnifiedOtherService)[] = [];
					const processItems = (
						items: (UnifiedFeeItem | UnifiedOtherService)[],
					) => {
						items.forEach((item) => {
							if (!item.parent_id) {
								allItems.push(item);
							}
							if (item.children && item.children.length > 0) {
								processItems(item.children);
							}
						});
					};
					processItems(section.data);
					return allItems.filter(
						(item) => item.line_item_id !== currentDraggedItem.line_item_id,
					);
				};

				// Calculate display_order for root items (parent_id is null)
				let newDisplayOrder: number | undefined = undefined;
				if (!newParentId) {
					// Item is being moved to section as root - calculate display_order
					const siblings = getSiblingsInSection(sectionName);

					// For gap drops, calculate display_order to insert between items
					if (overId.startsWith('gap-')) {
						const gapData = overData;
						const itemAbove = gapData?.itemAbove as
							| UnifiedFeeItem
							| UnifiedOtherService
							| undefined;
						const itemBelow = gapData?.itemBelow as
							| UnifiedFeeItem
							| UnifiedOtherService
							| undefined;

						if (itemAbove && itemBelow) {
							// Insert between two items - calculate order between them
							const orderAbove = itemAbove.display_order ?? 0;
							const orderBelow = itemBelow.display_order ?? 0;
							// If orders are consecutive, we need to reorder or use a fractional value
							if (orderBelow - orderAbove > 1) {
								// There's space between them, use middle value
								newDisplayOrder = orderAbove + (orderBelow - orderAbove) / 2;
							} else {
								// Orders are consecutive, use orderAbove + 0.5
								newDisplayOrder = orderAbove + 0.5;
							}
						} else if (itemAbove) {
							// Only item above - insert after it
							const orderAbove = itemAbove.display_order ?? 0;
							newDisplayOrder = orderAbove + 1;
						} else if (itemBelow) {
							// Only item below - insert before it
							const orderBelow = itemBelow.display_order ?? 0;
							newDisplayOrder = Math.max(0, orderBelow - 1);
						} else {
							// No items around - drop at end
							const maxOrder = Math.max(
								...siblings.map((s) => s.display_order ?? 0),
								0,
							);
							newDisplayOrder = maxOrder + 1;
						}
					} else if (targetItem) {
						// Dropping on a specific item (category drop near an item) - calculate relative position
						newDisplayOrder = calculateDisplayOrder(
							targetItem,
							siblings,
							isAbove,
						);
					} else {
						// Dropping at end of section (category drop without target item)
						const maxOrder = Math.max(
							...siblings.map((s) => s.display_order ?? 0),
							0,
						);
						newDisplayOrder = maxOrder + 1;
					}
				}

				const collectUpdates = (
					item: UnifiedFeeItem | UnifiedOtherService,
					newSection: string,
					newParentId: string | null,
					_newParentIdIcPlus: string | null, // Unused - backend handles counterpart
					_isRoot: boolean, // Unused - kept for API compatibility
					displayOrder?: number | undefined, // Display order for root items
				) => {
					const itemUpdates: BulkLineItemUpdate[] = [];

					// Use simplified move API: current_item_id + target_item_id (or target_section)
					// Use item.id (database UUID) if available, otherwise item.line_item_id
					// For IC+-only items, also check line_item_id_ic_plus as fallback
					const itemId =
						item.id || item.line_item_id || item.line_item_id_ic_plus;
					if (!itemId) {
						console.warn(
							'Item missing all ID fields (id, line_item_id, line_item_id_ic_plus):',
							item,
						);
						return itemUpdates; // Skip this item if no ID
					}

					// If moving to a parent (becoming a child), use target_item_id
					if (newParentId) {
						itemUpdates.push({
							current_item_id: itemId,
							target_item_id: newParentId,
						});
					}
					// If moving to section as root (no parent), use target_section
					else {
						const update: BulkLineItemUpdate = {
							current_item_id: itemId,
							target_section: newSection,
						};
						// Include display_order if specified (for gap drops to insert between items)
						if (displayOrder !== undefined) {
							update.display_order = displayOrder;
						}
						itemUpdates.push(update);
					}

					// NOTE: We intentionally DO NOT send updates for IC+ counterpart items here.
					// The backend's LineItemMoveService.move_item() automatically syncs the
					// counterpart (IC+ for IC_PLUS/TIERED, flat_rate for FLAT_TIERED).
					// Sending explicit IC+ updates causes double-processing which can lead
					// to duplicate items or unexpected behavior.

					// NOTE: We also do NOT send separate updates for children here.
					// The backend's _move_item_recursive() automatically moves all children
					// when moving a parent item. Sending explicit child updates would cause
					// double-processing similar to the IC+ issue above.

					return itemUpdates;
				};

				// Validate that we have the correct dragged item
				// For IC+-only items, also check line_item_id_ic_plus as fallback
				const draggedItemId =
					currentDraggedItem?.id ||
					currentDraggedItem?.line_item_id ||
					currentDraggedItem?.line_item_id_ic_plus;
				if (!currentDraggedItem || !draggedItemId) {
					console.error(
						'Invalid dragged item (missing all ID fields):',
						currentDraggedItem,
					);
					return;
				}

				const updates = collectUpdates(
					currentDraggedItem,
					sectionName,
					newParentId,
					newParentIdIcPlus,
					true,
					newDisplayOrder,
				);

				// Validate that we only have 1 update (the parent item)
				// Children and IC+ counterparts are handled by the backend automatically
				if (updates.length !== 1) {
					console.warn(
						'Expected exactly 1 update for the parent item, got:',
						updates.length,
					);
				}
				if (
					updates.length > 0 &&
					updates[0].current_item_id !== draggedItemId
				) {
					console.error('Update ID mismatch:', {
						expected: draggedItemId,
						actual: updates[0].current_item_id,
					});
				}

				// Save current position (before move) for undo - this becomes the "previous" position
				const previousSection = sourceCategory?.replace('category_', '') || '';
				const previousParentId = currentDraggedItem.parent_id || null;
				const previousDisplayOrder = currentDraggedItem.display_order;

				// Call the bulk update API
				const result = await bulkUpdateLineItems({
					updates,
					fileId: document.id,
				}).unwrap();

				// Show warnings if any (e.g., formula changes, missing data)
				if (result.warnings && result.warnings.length > 0) {
					result.warnings.forEach((warning) => {
						showToast('Move Notice', warning.message, 'warning', {
							duration: 8000,
						});
					});
				}

				// Switch to user_edits view mode so the user can see the updated values
				// Move operations update parent amounts, which should be visible immediately
				setViewMode('user_edits');

				// Manually trigger refetch to ensure parent amounts are updated in UI
				// RTK Query's invalidatesTags should handle this, but we call it explicitly
				// to ensure the UI updates immediately with the latest parent amounts
				if (onLineItemUpdated) {
					onLineItemUpdated();
				}

				// Add to drop history stack (push new move to stack)
				// Store all possible IDs to ensure undo button shows correctly
				setDropHistory((prev) => [
					...prev,
					{
						itemId: currentDraggedItem.line_item_id,
						itemIdIcPlus: currentDraggedItem.line_item_id_ic_plus || null,
						item: {
							...currentDraggedItem,
							// Ensure we have all ID fields for matching
							id: currentDraggedItem.id || currentDraggedItem.line_item_id,
							line_item_id: currentDraggedItem.line_item_id,
							line_item_id_ic_plus:
								currentDraggedItem.line_item_id_ic_plus || undefined,
						},
						previousSection,
						previousParentId,
						previousParentIdIcPlus: null, // Will be populated if needed from parent item
						previousDisplayOrder,
						newSection: sectionName,
						newParentId,
						newParentIdIcPlus,
						newDisplayOrder,
						timestamp: Date.now(),
					},
				]);

				// Auto-expand the target parent if we added as a child
				if (targetParentForExpand) {
					setExpandedRows((prev) => {
						const next = new Set(prev);
						next.add(targetParentForExpand.line_item_id);
						return next;
					});
				}

				// Also auto-expand the dragged item if it has children
				if (
					currentDraggedItem.children &&
					currentDraggedItem.children.length > 0
				) {
					setExpandedRows((prev) => {
						const next = new Set(prev);
						next.add(currentDraggedItem.line_item_id);
						return next;
					});
				}

				// Highlight the dropped item for 10 seconds
				const droppedLineItemId = currentDraggedItem.line_item_id;
				setRecentlyDroppedIds((prev) => {
					const next = new Set(prev);
					next.add(droppedLineItemId);
					return next;
				});

				// Remove the highlight after 10 seconds
				setTimeout(() => {
					setRecentlyDroppedIds((prev) => {
						const next = new Set(prev);
						next.delete(droppedLineItemId);
						return next;
					});
				}, 10000);

				if (onLineItemUpdated) {
					onLineItemUpdated();
				}
			} catch (error) {
				console.error('Error bulk updating line items:', error);
				alert(
					`Failed to move line item: ${error instanceof Error ? error.message : 'Unknown error'}`,
				);
			}
		},
		[
			draggedItem,
			draggedFromCategory,
			document.id,
			bulkUpdateLineItems,
			onLineItemUpdated,
			getAllDescendantIds,
			analysisData,
			showToast,
		],
	);

	const handleDragCancel = useCallback(() => {
		setActiveId(null);
		setDraggedItem(null);
		setDraggedFromCategory(null);
	}, []);

	// Undo the last drag-drop operation (supports both session-based and persistent undo)
	const handleUndoDrop = useCallback(
		async (item?: UnifiedFeeItem | UnifiedOtherService) => {
			// If item is provided and has move_history, use persistent undo
			const moveHistory = item?.extra_attributes?.move_history;
			const hasMoveHistory =
				Array.isArray(moveHistory) && moveHistory.length > 0;
			if (item && hasMoveHistory && !isUndoing) {
				setIsUndoing(true);
				try {
					// Use the update_line_item API with action: 'undo_move'
					// Use item.id (database UUID) if available, otherwise fall back to line_item_id
					// This handles IC+-only items where line_item_id might be empty
					const primaryItemId = item.id || item.line_item_id;

					if (!primaryItemId) {
						console.error('Cannot undo: item has no valid ID', { item });
						alert('Cannot undo: item has no valid ID');
						setIsUndoing(false);
						return;
					}

					// NOTE: Backend's undo_move handles BOTH processor pair items (IC/IC+ or flat_tiered/flat_rate)
					// automatically, so we only need to call it once. The backend will:
					// 1. Find and restore the counterpart item
					// 2. Remove the last move from move_history for both items
					await updateLineItem({
						lineItemId: primaryItemId,
						fileId: document.id,
						updates: {
							action: 'undo_move',
						},
					}).unwrap();

					// Refresh data to show updated positions
					// Clear all previous undo states (session history) after undo
					setDropHistory([]);
					if (onLineItemUpdated) {
						onLineItemUpdated();
					}
				} catch (error) {
					console.error('Error undoing move:', error);
					alert(
						`Failed to undo move: ${error instanceof Error ? error.message : 'Unknown error'}`,
					);
					// Refresh data even on error to ensure UI reflects actual server state
					if (onLineItemUpdated) {
						onLineItemUpdated();
					}
				} finally {
					setIsUndoing(false);
				}
				return;
			}

			// Otherwise, use session-based undo from dropHistory stack
			if (!dropHistory || dropHistory.length === 0 || isUndoing) {
				return;
			}

			setIsUndoing(true);

			try {
				// Pop the last move from the stack
				const lastMove = dropHistory[dropHistory.length - 1];
				const remainingHistory = dropHistory.slice(0, -1);

				const collectUpdates = (
					item: UnifiedFeeItem | UnifiedOtherService,
					newSection: string,
					newParentId: string | null,
					_newParentIdIcPlus: string | null, // Unused - backend handles counterpart
					_newDisplayOrder: number | undefined, // Unused - backend calculates
					isRoot: boolean,
				) => {
					const itemUpdates: BulkLineItemUpdate[] = [];

					// Use item.id (database UUID) if available, otherwise fall back to line_item_id
					// For IC+-only items, also check line_item_id_ic_plus as fallback
					const itemId =
						item.id || item.line_item_id || item.line_item_id_ic_plus;
					if (!itemId) {
						console.warn(
							'Item missing all ID fields (id, line_item_id, line_item_id_ic_plus) in undo:',
							item,
						);
						return itemUpdates; // Skip this item
					}

					// Use simplified move API: current_item_id + target_item_id (or target_section)
					// For root items moving to section
					if (isRoot && !newParentId) {
						itemUpdates.push({
							current_item_id: itemId,
							target_section: newSection,
						});
					}
					// For root items moving to a parent
					else if (isRoot && newParentId) {
						itemUpdates.push({
							current_item_id: itemId,
							target_item_id: newParentId,
						});
					}
					// For children, they keep their parent but change section
					else {
						itemUpdates.push({
							current_item_id: itemId,
							target_section: newSection,
						});
					}

					// NOTE: Do NOT add IC+/flat_rate counterpart items to the update list.
					// The backend's move_item handles BOTH processor pair items automatically:
					// 1. Finds the counterpart item
					// 2. Moves it to the same section
					// 3. Syncs attributes between primary and counterpart
					// Adding duplicate moves would cause errors or double-processing.

					// NOTE: We also do NOT send separate updates for children here.
					// The backend's _move_item_recursive() automatically moves all children
					// when moving a parent item. Sending explicit child updates would cause
					// double-processing similar to the IC+ issue above.

					return itemUpdates;
				};

				// Restore to previous position (from the last move in history)
				const updates = collectUpdates(
					lastMove.item,
					lastMove.previousSection,
					lastMove.previousParentId,
					lastMove.previousParentIdIcPlus,
					lastMove.previousDisplayOrder,
					true,
				);

				const undoResult = await bulkUpdateLineItems({
					updates,
					fileId: document.id,
				}).unwrap();

				// Show warnings if any (e.g., formula changes when moving back)
				if (undoResult.warnings && undoResult.warnings.length > 0) {
					undoResult.warnings.forEach((warning) => {
						showToast('Undo Notice', warning.message, 'warning', {
							duration: 8000,
						});
					});
				}

				// Highlight the restored item for 10 seconds
				const restoredItemId = lastMove.itemId;
				setRecentlyDroppedIds((prev) => {
					const next = new Set(prev);
					next.add(restoredItemId);
					return next;
				});

				setTimeout(() => {
					setRecentlyDroppedIds((prev) => {
						const next = new Set(prev);
						next.delete(restoredItemId);
						return next;
					});
				}, 10000);

				// Auto-expand the previous parent if the item was a child
				if (lastMove.previousParentId) {
					setExpandedRows((prev) => {
						const next = new Set(prev);
						next.add(lastMove.previousParentId!);
						return next;
					});
				}

				// Also auto-expand the item itself if it has children
				if (lastMove.item.children && lastMove.item.children.length > 0) {
					setExpandedRows((prev) => {
						const next = new Set(prev);
						next.add(lastMove.itemId);
						return next;
					});
				}

				// Undo should delete all previous states (clear local undo history)
				setDropHistory([]);

				if (onLineItemUpdated) {
					onLineItemUpdated();
				}
			} catch (error) {
				console.error('Error undoing drop:', error);
				alert(
					`Failed to undo move: ${error instanceof Error ? error.message : 'Unknown error'}`,
				);
				// Refresh data even on error to ensure UI reflects actual server state
				if (onLineItemUpdated) {
					onLineItemUpdated();
				}
			} finally {
				setIsUndoing(false);
			}
		},
		[
			dropHistory,
			isUndoing,
			bulkUpdateLineItems,
			document.id,
			onLineItemUpdated,
			showToast,
		],
	);

	const getStatusBadge = () => {
		if (status === 'processing') {
			return (
				<span className="ml-2 px-2 py-1 text-primary-800 text-xs bg-primary-50 rounded-full font-primary">
					Expect Analysis Soon
				</span>
			);
		}
		if (status === 'completed') {
			return (
				<span className="ml-2 px-2 py-1 text-success-800 text-xs bg-success-100 rounded-full font-primary">
					Analysis Ready
				</span>
			);
		}
		return null;
	};

	// Common props for all AnalysisTable instances
	const commonTableProps = {
		processingMonth: analysisData.processingMonth,
		fileId: document.id,
		processorName: analysisData.processorName,
		processorNamePlus: analysisData.processorNamePlus,
		isLoading: false,
		isFetching: false,
		showChildren: true, // Default to collapsed for cleaner view
		enableEditing,
		onLineItemUpdated,
		expandedRows,
		onToggleRow: handleToggleRow,
		recentlyDroppedIds,
		// View mode change callback - switch to user_edits when user makes an edit
		onViewModeChange: setViewMode,
		// Undo functionality props
		isCurrentProcessor: true, // This table represents IC (left side) processor
		onUndoDrop: handleUndoDrop,
		undoableItemIds: (() => {
			if (!dropHistory || dropHistory.length === 0) {
				return new Set<string>();
			}
			const ids = new Set<string>();
			dropHistory.forEach((h) => {
				// Add line_item_id (IC item ID)
				if (h.itemId) ids.add(h.itemId);
				// Add line_item_id_ic_plus (IC+ item ID)
				if (h.itemIdIcPlus) ids.add(h.itemIdIcPlus);
				// Also add item.id if available (for items that might use id instead of line_item_id)
				if (h.item?.id) ids.add(h.item.id);
				// Add line_item_id from the item object as well (in case it differs)
				if (h.item?.line_item_id) ids.add(h.item.line_item_id);
				// Add line_item_id_ic_plus from the item object as well
				if (h.item?.line_item_id_ic_plus) ids.add(h.item.line_item_id_ic_plus);
			});
			return ids;
		})(),
		isUndoing,
	};

	// Skeleton loading sections data
	const skeletonSections = analysisSections.map((section) => ({
		title: section.title,
		skeletonRows: section.skeletonRows,
	}));

	// Grand total metrics data - prefer backend-calculated totals when available
	const currentTotal = calculatedTotals?.ic ?? analysisData.totals.ic;
	const comparisonTotal =
		calculatedTotals?.icPlus ?? analysisData.totals.icPlus;
	const yearlySavings =
		calculatedTotals?.savings?.yearly ?? monthlySavingsData.value * 12;

	const totalMetrics: Array<{
		label: string;
		value: string;
		valueColor: string;
	}> = [
		{
			label: 'Current Total',
			value: formatCurrency(currentTotal),
			valueColor: 'text-zinc-800',
		},
		{
			label: 'Cost Comparison',
			value: formatCurrency(comparisonTotal),
			valueColor: 'text-zinc-800',
		},
		{
			label: 'Monthly Savings',
			value: formatCurrency(monthlySavingsData.value),
			valueColor: monthlySavingsData.isNegative
				? 'text-red-500'
				: 'text-green-500',
		},
		{
			label: 'Yearly Savings',
			value: formatCurrency(yearlySavings),
			valueColor: monthlySavingsData.isNegative
				? 'text-red-500'
				: 'text-green-500',
		},
	];

	const renderAnalysisContent = () => {
		// Check if data is still loading (all totals are zero or data arrays are empty)
		const isDataLoading = status === 'completed' && (isLoading || isFetching);

		if (status === 'completed') {
			if (isDataLoading) {
				return (
					<div className="mb-16 z-10 relative space-y-6">
						{skeletonSections.map((section, index) => (
							<AnalysisSectionSkeleton
								key={index}
								title={section.title}
								skeletonRows={section.skeletonRows}
							/>
						))}

						{/* Grand Total Section - Skeleton */}
						<div className="w-full mt-4 px-5 py-5 bg-neutral-100 border-t border-gray-100 inline-flex justify-start items-center">
							<div className="w-full flex justify-start items-center gap-6">
								{[...Array(4)].map((_, index) => (
									<div
										key={index}
										className="w-full px-4 rounded-md flex justify-center items-center gap-2"
									>
										<SkeletonMetric showLabel={true} />
									</div>
								))}
							</div>
						</div>
					</div>
				);
			}

			// Determine if the dragged item has children
			const draggedItemHasChildren =
				draggedItem?.children && draggedItem.children.length > 0;

			return (
				<DndContext
					sensors={sensors}
					collisionDetection={preferLineItemCollision}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}
				>
					<div className="mb-16 relative">
						{analysisSections
							.filter((section) => section.data && section.data.length > 0) // Hide empty sections
							.map((section, index) => (
								<AnalysisTable
									key={index}
									title={section.title}
									data={section.data}
									columns={section.columns}
									{...commonTableProps}
									activeId={activeId}
									draggedFromCategory={draggedFromCategory}
									draggedItemHasChildren={!!draggedItemHasChildren}
									sectionKey={section.sectionKey}
									// Show rate disclaimer for Authorization & Discount section on flat-tiered statements
									showRateDisclaimer={
										section.sectionKey === 'authorization_and_discount' &&
										calculatedTotals?.show_rate_disclaimer
									}
								/>
							))}

						{/* Grand Total Section */}
						<GrandTotalSection metrics={totalMetrics} />
					</div>

					{/* Drag Overlay - Ghost preview of the dragged item */}
					<DragOverlay
						dropAnimation={{
							duration: 200,
							easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
						}}
					>
						{draggedItem ? (
							<div className="bg-white border-2 border-blue-500 rounded-lg shadow-2xl px-4 py-2 opacity-90 min-w-[200px] max-w-[400px]">
								<div className="flex items-center gap-2">
									<GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
									<span className="text-sm font-medium text-gray-800 truncate">
										{draggedItem.type}
									</span>
									{draggedItemHasChildren && (
										<span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
											{draggedItem.children?.length} children
										</span>
									)}
								</div>
								{draggedItemHasChildren && (
									<div className="mt-1 text-xs text-gray-500 pl-6">
										Moving with all children
									</div>
								)}
							</div>
						) : null}
					</DragOverlay>
				</DndContext>
			);
		}
		return null;
	};

	return (
		<div
			className={`transition-all duration-500 ${showProgress ? 'opacity-50' : ''}`}
		>
			{/* Step Header - Static Design */}
			<div className="w-full px-7 py-3 bg-white inline-flex justify-start items-start">
				<div className="rounded-lg flex justify-start items-center gap-3">
					<div className="w-9 h-9 bg-violet-100 rounded-full inline-flex flex-col justify-center items-center">
						<BarChart3 className="w-5 h-5 text-indigo-700" />
					</div>
					<div className="inline-flex flex-col justify-start items-start">
						<div className="inline-flex justify-start items-center gap-2">
							<div className="justify-center text-black text-base font-medium leading-tight font-primary">
								Fee Analysis
							</div>
							{getStatusBadge()}
						</div>
					</div>
				</div>
			</div>
			{renderAnalysisContent()}

			{/* Info Modal for drag-drop validation messages */}
			{infoModal.isOpen && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 font-primary">
						{/* Modal Header */}
						<div className="flex items-center justify-between p-6 border-b border-neutral-200">
							<div className="flex items-center">
								<div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
									<svg
										className="w-5 h-5 text-amber-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<div className="ml-4">
									<h3 className="text-lg font-semibold text-neutral-900 font-primary">
										{infoModal.title}
									</h3>
								</div>
							</div>
							<button
								onClick={() =>
									setInfoModal({ isOpen: false, title: '', message: '' })
								}
								className="text-neutral-400 hover:text-neutral-600 transition-colors"
							>
								<svg
									className="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						{/* Modal Body */}
						<div className="p-6">
							<p className="text-neutral-700 font-primary">
								{infoModal.message}
							</p>
						</div>

						{/* Modal Footer */}
						<div className="flex justify-end p-6 bg-neutral-50 rounded-b-xl">
							<button
								onClick={() =>
									setInfoModal({ isOpen: false, title: '', message: '' })
								}
								className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-primary"
							>
								Got it
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AnalysisStep;
